/**
 * What has been won, in a table.
 *
 * Every row here came off the pipeline by being won, or was typed in as a
 * deal outright, so there is no stage to filter by and nothing to weight: the
 * columns are who the customer is, how to reach them, where they came from,
 * what they are worth, when they closed, and whether the account they
 * registered has been linked yet — which is the one thing left to do.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../store/AppContext.jsx';
import { parseDate, formatDate, periodLabel } from '../lib/dates.js';
import api from '../lib/api.js';
import { download } from '../lib/format.js';
import Icon from '../lib/icons.jsx';
import {
  Card, CardHead, EmptyState, Avatar, StagePill, OwnerPill, Dash, SegmentPill
} from '../components/Primitives.jsx';
import { TileRow } from '../components/StatCards.jsx';
import CrmFilter from '../components/CrmFilter.jsx';
import DroppedList from '../components/DroppedList.jsx';
import { useWindowed } from '../components/MoreRows.jsx';
import DealCards from '../components/DealCards.jsx';
import DealsGrid from '../components/DealsGrid.jsx';
import ViewToggle from '../components/ViewToggle.jsx';
import PlanChip from '../components/PlanChip.jsx';
import {
  filterDeals, dealsOf, isOpen, companyName, clientOfDeal, leadDetails, lastNote, originOf,
  dealValue, dealValueTotal
} from '../domain/crm.js';

/*
 * Where a lead came from, in four or five kinds rather than one.
 *
 * The tracker writes whatever the ad platform called it - "Organic Search",
 * "Ads", "Referral", "Direct" and the odd one nobody has seen before - and a
 * column of identical grey tags is a column nobody reads down. Matched
 * loosely on purpose: a source nobody has catered for falls through to the
 * plain tag rather than to nothing.
 */
const srcKind = s => {
  const v = String(s || '').toLowerCase();
  if (/ad|ppc|paid|pmax/.test(v)) return 'ads';
  if (/organic|search|seo/.test(v)) return 'organic';
  if (/refer/.test(v)) return 'referral';
  if (/direct/.test(v)) return 'direct';
  if (/social|insta|face|linked|tik/.test(v)) return 'social';
  if (/manual/.test(v)) return 'manual';
  return '';
};

/* What each tab is called on the page, so a document says which one it is. */
const TAB_LABEL = {
  open: 'Still to move',
  moved: 'Moved to clients',
  all: 'All deals',
  dropped: 'Not interested',
  quiet: 'No response'
};

const SRC_ICON = {
  ads: 'zap', organic: 'search', referral: 'users',
  direct: 'link', social: 'star', manual: 'edit'
};

export default function Deals() {
  const {
    S, crm, rows, cF, setCF, crmMode, pipeline, fmtMoney, setSheet, removeFromCrm, convertDeal, unconvertDeal,
    saveDeal, toast, managesBoard
  } = useApp();
  const [tab, setTab] = useState('open');
  const navigate = useNavigate();

  const pick = key => setCF(f => ({ ...f, tile: f.tile === key ? '' : key }));
  const tileOn = key => cF.tile === key;

  /* Converted deals have left for the Clients page; this is what is still
     here to be moved. */
  const all = useMemo(() => filterDeals(crm, cF, 'deal', rows), [crm, cF, rows]);
  const open_ = all.filter(d => !clientOfDeal(rows, d));
  const moved = all.filter(d => clientOfDeal(rows, d));

  /* Closed off. Out of every count above, listed on its own tab with the
     reason attached — see DroppedList. */
  const dropped = useMemo(
    () => filterDeals(crm, cF, 'deal:dropped', rows), [crm, cF, rows]);
  /* Said no, or never answered. Two tabs, because they are two piles -
     see the note on the same split in Pipeline. */
  const saidNo = useMemo(() => dropped.filter(d => d.dropKind !== 'quiet'), [dropped]);
  const noReply = useMemo(() => dropped.filter(d => d.dropKind === 'quiet'), [dropped]);
  const closedTab = tab === 'dropped' || tab === 'quiet';

  /*
   * Whose moves these were.
   *
   * A manager's question on the moved tab is not how many went across, it is
   * how many Geo took across — and the answer has to come off the same list
   * the tab is showing, or the number on the tab and the number in the filter
   * disagree. Only offered where there is a choice to make: an executive sees
   * their own rows and nobody else's, so a dropdown of one name is a control
   * that asks a question with one answer.
   */
  const [who, setWho] = useState('');
  /*
   * Unowned is an answer, not a gap.
   *
   * Filtering the blanks out left a dropdown of the two people who happened
   * to have their name on something, and hid the fact that forty-seven rows
   * went across with nobody's name on them at all — which is the first thing
   * a manager looking at this tab needs to know. It sorts last, because it is
   * a pile rather than a person.
   */
  const NOBODY = '__nobody';
  const movers = useMemo(() => {
    const names = [...new Set(moved.map(d => (d.owner || '').trim()).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b));
    return moved.some(d => !(d.owner || '').trim()) ? [...names, NOBODY] : names;
  }, [moved]);
  const countFor = name => moved.filter(d => (name === NOBODY
    ? !(d.owner || '').trim() : (d.owner || '').trim() === name)).length;
  const byWho = who
    ? moved.filter(d => (who === NOBODY ? !(d.owner || '').trim() : (d.owner || '').trim() === who))
    : moved;

  /* Which half the table is showing. A fold under the list hid the larger
     half of the page behind a click nobody knew was there; as a tab it is
     one of two places the same table can be, and the counts say which is
     which before either is opened. */
  const shown = tab === 'moved' ? byWho
    : tab === 'dropped' ? saidNo
      : tab === 'quiet' ? noReply
        : tab === 'all' ? all : open_;
  /* The same hundred-at-a-time as every other list on the book. */
  const [page, moreRows] = useWindowed(shown);

  /* Import and Export act on the table as it is filtered right now, so they
     sit on the filter bar beside the control that decides that. An imported
     row is a deal already - this page lists nothing else. */
  /*
   * The list as it is on screen, as a laid-out workbook.
   *
   * It used to be a CSV written here: no widths, no header, a date that
   * opened as ######## and a notes paragraph that swallowed the sheet. The
   * rows go to the server, which lays them out with the same styling as the
   * demo report, because what people do with this file is read it and send
   * it on. The rows are sent rather than a filter, so what comes out is the
   * tab, the filters and the order in front of them.
   */
  const [saving, setSaving] = useState(false);
  const [pdfing, setPdfing] = useState(false);
  /*
   * One row, two documents.
   *
   * The workbook and the PDF are built from the same shape, because a column
   * added to one quietly never reaching the other is how two exports of the
   * same screen start disagreeing. They read `shown` — which is the tab, the
   * filters and the owner dropdown, all applied — so what comes out is what
   * is in front of whoever pressed the button. Exporting anything else is the
   * one thing an export must never do.
   */
  const reportRows = () => shown.map(d => {
    const client = clientOfDeal(rows, d);
    const v = dealValue(rows, d);
    const f = leadDetails(crm, d);
    const close = parseDate(d.close);
    return {
            title: d.title,
            client: client ? client.name : '',
            free: v.free,
            email: f.email || '',
            phone: f.phone || '',
            company: companyName(crm, d.cid) || '',
            city: f.city || '',
            country: f.country || '',
            source: d.source || '',
            campaign: f.campaign || '',
            plan: client ? client.plan || d.plan || '' : d.plan || '',
            cycle: client ? client.cycle || d.cycle || '' : d.cycle || '',
            /* Blank, not zero, where nothing is known - a zero would read as
               "worth nothing", which is a different statement. */
            value: v.free ? 0 : v.known ? v.amount : '',
            /* Both: the amount is what the invoice says, the monthly rate is
               what makes two rows on different cycles comparable. */
            monthly: v.free ? 0 : v.known ? v.monthly : '',
            wonOn: close ? formatDate(close, false, S.dateFmt) : '',
            owner: d.owner || '',
      manual: !d.origin,
      /* The PDF's "Closed" column reads this name; the workbook reads wonOn.
         Both carry the same date rather than one of them going blank. */
      closed: close ? formatDate(close, false, S.dateFmt) : '',
      notes: d.notes || ''
    };
  });

  /* What the document says it is, under its heading. Without it a filtered
     export is indistinguishable from the whole board a week later. */
  const filterLabel = () => [
    TAB_LABEL[tab],
    who && `moved by ${who === NOBODY ? 'nobody' : who}`,
    cF.q && `"${cF.q}"`,
    cF.stage && `stage ${cF.stage}`,
    cF.source && `source ${cF.source.split(',').join(' or ')}`,
    cF.plan && `plan ${cF.plan}`,
    cF.owner && `owner ${cF.owner}`,
    (cF.from || cF.to) && `${cF.from || '…'} to ${cF.to || '…'}`
  ].filter(Boolean).join(' · ');

  const onExport = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const out = reportRows();
      const blob = await api.deals.book({
        generatedAt: formatDate(new Date(), true, S.dateFmt),
        deals: out
      });
      download(blob, `xyra-deals-${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast(`Exported ${out.length} rows`);
    } catch (e) {
      toast(e.message || 'That export could not be made');
    } finally {
      setSaving(false);
    }
  };

  const onPdf = async () => {
    if (pdfing) return;
    setPdfing(true);
    try {
      const out = reportRows();
      const blob = await api.deals.reportPdf({
        kind: 'deals',
        rowsTitle: TAB_LABEL[tab],
        generatedAt: formatDate(new Date(), true, S.dateFmt),
        period: { label: filterLabel() },
        rows: out
      });
      download(blob, `xyra-deals-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast(`${out.length} rows as PDF`);
    } catch (e) {
      toast(e.message || 'That report could not be made');
    } finally {
      setPdfing(false);
    }
  };
  /* Export only. Import was a way to type deals in from a spreadsheet, and
     every row on this board arrives from the tracker or from the New deal
     box - a second door onto the same table, unused, beside the one people
     actually want. */
  /* The same pair as the pipeline carries, in the same place and the same
     colours — a spreadsheet to work in, a page to send on. */
  const tools = (
    <>
      <button
        type="button"
        className="btn fmtbtn xls"
        onClick={onExport}
        disabled={saving}
        title="Download exactly what the tab and the filters have left, as a formatted Excel workbook"
      >
        <Icon name={saving ? 'refresh' : 'sheet'} />{saving ? 'Preparing…' : 'Excel'}
      </button>
      <button
        type="button"
        className="btn fmtbtn pdf"
        onClick={onPdf}
        disabled={pdfing}
        title="The same rows, laid out for paper"
      >
        <Icon name={pdfing ? 'refresh' : 'file'} />{pdfing ? 'Preparing…' : 'PDF'}
      </button>
    </>
  );
  /* What the board is worth, and what it could not put a figure on. */
  const worth = dealValueTotal(rows, dealsOf(crm));

  /* The same for the wins whose close date falls in this month. */
  const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const wonMonth = dealValueTotal(rows, dealsOf(crm).filter(d => {
    if (d.stage !== 'Won') return false;
    const c = parseDate(d.close);
    return c && c >= monthStart;
  }));
  const linkedCount = dealsOf(crm).filter(d => clientOfDeal(rows, d)).length;

  /* Straight to the Clients page: the record is created, the deal linked,
     and the row leaves this table. One click, no picker. */
  const toClient = (e, d) => {
    e.stopPropagation();
    convertDeal(d.id);
  };


  const remove = (e, id) => {
    e.stopPropagation();
    // eslint-disable-next-line no-alert
    if (window.confirm('Delete this deal?')) removeFromCrm('deals', id);
  };

  return (
    <>
      <TileRow
        tiles={[
          /* Unfiltered on purpose: these six answer "how are we doing", and
             a number that moves when a dropdown changes cannot. Only the
             table below follows the filters. */
          ['Deals', dealsOf(crm).length, 'card', 'g-purple', () => { pick(''); setTab('all'); }, tab === 'all'],
          /* A monthly rate, not a pile of invoices added together. The
             amounts on the client records are per billing cycle, so summing
             an annual 950 with a monthly 49 printed a year and a month as
             one figure; each is now divided by the months it covers. */
          ['Value', `${fmtMoney(worth.monthly)}/mo`, 'chart', 'g-green', () => setSheet({ kind: 'dealValue' }), false,
            [worth.known ? `${worth.known} priced` : '',
              worth.annualBilled ? `${worth.annualBilled} billed yearly` : '',
              worth.unknown ? `${worth.unknown} not priced` : '',
              worth.free ? `${worth.free} free` : ''].filter(Boolean).join(' · ')],
          /* Counted the same way, from the client record rather than from a
             value column nothing fills in. */
          ['Won this month', `${fmtMoney(wonMonth.monthly)}/mo`, 'star', 'g-blue',
            () => setSheet({ kind: 'dealValue', scope: 'month' }), false,
            wonMonth.unknown || wonMonth.free
              ? `${wonMonth.known + wonMonth.unknown + wonMonth.free} won`
                + (wonMonth.unknown ? ` · ${wonMonth.unknown} not priced` : '')
              : ''],
          /* These two are the two tabs, so the tile is the tab. */
          ['Moved to clients', linkedCount, 'users', 'g-cyan', () => setTab('moved'), tab === 'moved'],
          ['Still to move', open_.length, 'refresh', 'g-amber', () => setTab('open'), tab === 'open'],
          ['On the pipeline', crm.deals.filter(isOpen).length, 'zap', 'g-pink', () => navigate('/pipeline')]
        ]}
      />

      <CrmFilter kind="deal" tools={tools} />

      {/* Two halves of one board: what is still to be worked, and what has
          already become a client. Both are deals, so both are here - the tab
          decides which the table below is showing. */}
      <nav className="dbk-tabs" role="tablist">
        {[
          ['open', 'To move', 'refresh', open_.length],
          ['moved', 'Moved to clients', 'users', moved.length],
          ['all', 'All deals', 'card', all.length],
          /* Last, because it is the one nobody is working. */
          ['dropped', 'Not interested', 'x', saidNo.length],
          ['quiet', 'No response', 'eyeoff', noReply.length]
        ].map(([k, label, icon, n]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={tab === k}
            className={`dbk-tab${tab === k ? ' on' : ''}`}
            onClick={() => setTab(k)}
          >
            <Icon name={icon} solid />
            {label}
            <span className="dbk-tabn">{n}</span>
          </button>
        ))}
      </nav>

      <Card>
        <CardHead icon="card" tone="g-orange" title="Deals" count={shown.length}>
          {/* Only on the tab it answers a question about, and only when
              there is more than one name to choose between. */}
          {tab === 'moved' && managesBoard && movers.length ? (
            <label className="dbk-who">
              <Icon name="users" />
              <select value={who} onChange={e => setWho(e.target.value)}>
                <option value="">Everyone ({moved.length})</option>
                {movers.map(n => (
                  <option key={n} value={n}>
                    {`${n === NOBODY ? 'Nobody assigned' : n} (${countFor(n)})`}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {closedTab ? null : <ViewToggle view="deals" />}
        </CardHead>

        {closedTab ? (
          <DroppedList rows={shown} kind={tab === 'quiet' ? 'quiet' : 'no'} />
        ) : crmMode.deals === 'grid' ? (
          <DealCards list={shown} onRemove={remove} />
        ) : (
          <DealsGrid list={shown} tab={tab} />
        )}

      </Card>
    </>
  );
}
